import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase';
import { 
  X, 
  Search, 
  Download,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import Papa from 'papaparse';

interface ResponseListModalProps {
  isOpen: boolean;
  onClose: () => void;
  surveyId: string;
  questionId: string;
  questionText: string;
}

interface Response {
  id: string;
  answer: string;
  submitted_at: string;
  respondent: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export function ResponseListModal({
  isOpen,
  onClose,
  surveyId,
  questionId,
  questionText
}: ResponseListModalProps) {
  const [responses, setResponses] = useState<Response[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    if (isOpen) {
      loadResponses();
    }
  }, [isOpen, page, sortOrder, searchTerm]);

  const loadResponses = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('survey_responses')
        .select(`
          id,
          answer,
          submitted_at,
          survey_respondents!inner (
            first_name,
            last_name,
            email
          )
        `, { count: 'exact' })
        .eq('template_id', surveyId)
        .eq('question_id', questionId)
        .order('submitted_at', { ascending: sortOrder === 'asc' });

      if (searchTerm) {
        query = query.ilike('answer', `%${searchTerm}%`);
      }

      // Add pagination
      const start = (page - 1) * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE - 1;
      query = query.range(start, end);

      const { data, count, error } = await query;

      if (error) throw error;

      // Format responses
      const formattedResponses = data?.map(r => ({
        id: r.id,
        answer: r.answer,
        submitted_at: r.submitted_at,
        respondent: {
          first_name: r.survey_respondents.first_name,
          last_name: r.survey_respondents.last_name,
          email: r.survey_respondents.email
        }
      })) || [];

      setResponses(formattedResponses);
      setTotalPages(Math.ceil((count || 0) / ITEMS_PER_PAGE));

    } catch (err) {
      console.error('Error loading responses:', err);
      setError('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Get all responses without pagination
      const { data, error } = await supabase
        .from('survey_responses')
        .select(`
          answer,
          submitted_at,
          survey_respondents!inner (
            first_name,
            last_name,
            email
          )
        `)
        .eq('template_id', surveyId)
        .eq('question_id', questionId)
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      // Format data for CSV
      const csvData = data.map(r => ({
        'First Name': r.survey_respondents.first_name,
        'Last Name': r.survey_respondents.last_name,
        'Email': r.survey_respondents.email,
        'Response': r.answer,
        'Submitted At': new Date(r.submitted_at).toLocaleString()
      }));

      // Generate CSV
      const csv = Papa.unparse(csvData);
      
      // Create download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey-responses-${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error('Error exporting responses:', err);
      setError('Failed to export responses');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Response List
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {questionText}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1); // Reset to first page on search
                }}
                placeholder="Search responses..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="whitespace-nowrap"
            >
              <ArrowUpDown className="h-4 w-4 mr-2" />
              {sortOrder === 'asc' ? 'Newest First' : 'Oldest First'}
            </Button>

            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isExporting || responses.length === 0}
              className="whitespace-nowrap"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : responses.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No responses found
            </div>
          ) : (
            <div className="space-y-4">
              {responses.map((response) => (
                <div
                  key={response.id}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {response.respondent.first_name} {response.respondent.last_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(response.submitted_at).toLocaleString()}
                    </div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {response.answer}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}